import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, MapPin, CheckCircle, X, FileText } from 'lucide-react';
import { offerColorStyles } from '../utils/offerColor';

/**
 * Contact form step: customer info form only.
 * Used in StepperChatbotWidget after OfferOverviewStep.
 * Fetches offer for validation; on accept calls API and onSuccess(customerInfo).
 */
const ContactFormStep = ({
  offerId,
  token,
  onSuccess,
  onReject,
  compact = false,
  showRejectButton = true,
  showBackToChat = false,
  onBackToChat,
}) => {
  const { t } = useTranslation();
  const [offer, setOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    zip: '',
    country: ''
  });
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);

  useEffect(() => {
    if (offerId && token) {
      fetchOffer();
    }
  }, [offerId, token]);

  useEffect(() => {
    if (!offer || !offer.customerInfo) return;
    const info = offer.customerInfo;
    const hasAddress = info.street || info.city || info.zip || info.locationZip;
    if (hasAddress) {
      setCustomerInfo(prev => ({
        ...prev,
        name: info.name || prev.name,
        email: info.email || prev.email,
        phone: info.phone || prev.phone,
        street: info.street || '',
        city: info.city || '',
        zip: info.zip != null ? String(info.zip) : (info.locationZip != null ? String(info.locationZip) : prev.zip),
        country: info.country || ''
      }));
    }
  }, [offer]);

  const fetchOffer = async () => {
    try {
      if (!token) {
        setError(t('offer.invalidToken'));
        setIsLoading(false);
        return;
      }

      const response = await fetch(`/api/offers/${offerId}?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        setOffer(data.offer);
      } else {
        if (response.status === 403) setError(t('offer.invalidToken'));
        else if (response.status === 410) setError(t('offer.linkExpired'));
        else setError(data.error || t('offer.offerNotFound'));
      }
    } catch (err) {
      console.error('Error fetching offer:', err);
      setError(t('offer.failedToLoadOffer'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const validateCustomerInfo = () => {
    const zipValue = customerInfo.zip.trim() || (offer?.customerInfo?.locationZip ? String(offer.customerInfo.locationZip) : '');
    return customerInfo.name.trim() &&
      customerInfo.email.trim() &&
      customerInfo.phone.trim() &&
      customerInfo.street.trim() &&
      customerInfo.city.trim() &&
      zipValue;
  };

  const handleAcceptOffer = async () => {
    if (!validateCustomerInfo()) {
      alert(t('offer.fillRequiredFields'));
      return;
    }

    setIsSubmittingOffer(true);
    try {
      const updateResponse = await fetch(`/api/offers/${offerId}/customer-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          street: customerInfo.street,
          city: customerInfo.city,
          zip: customerInfo.zip.trim() || (offer?.customerInfo?.locationZip ? String(offer.customerInfo.locationZip) : ''),
          country: customerInfo.country || undefined
        }),
      });

      if (!updateResponse.ok) {
        throw new Error(t('offer.failedToUpdateCustomerInfo'));
      }

      const acceptResponse = await fetch(`/api/offers/${offerId}/accept`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!acceptResponse.ok) {
        const errorData = await acceptResponse.json();
        throw new Error(errorData.error || t('offer.failedToAcceptOffer'));
      }

      onSuccess?.(customerInfo);
    } catch (err) {
      console.error('Error processing offer:', err);
      alert(err.message || t('offer.errorProcessingOffer'));
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const handleRejectOffer = async () => {
    if (!window.confirm(t('offer.confirmReject'))) return;

    setIsSubmittingOffer(true);
    try {
      const rejectResponse = await fetch(`/api/offers/${offerId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Customer rejected the offer' }),
      });

      if (!rejectResponse.ok) {
        const errorData = await rejectResponse.json();
        throw new Error(errorData.error || t('offer.failedToAcceptOffer'));
      }

      alert(t('offer.offerRejected'));
      onReject?.();
    } catch (err) {
      console.error('Error rejecting offer:', err);
      alert(err.message || t('offer.errorRejectingOffer'));
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${compact ? 'py-8' : 'min-h-[200px]'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('offer.loadingOffer')}</p>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className={`flex items-center justify-center p-4 ${compact ? '' : 'min-h-[200px]'}`}>
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('common.error')}</h2>
          <p className="text-gray-600 mb-6">{error || t('offer.offerNotFound')}</p>
        </div>
      </div>
    );
  }

  if (offer.status !== null) {
    return (
      <div className={`flex items-center justify-center p-4 ${compact ? '' : 'min-h-[200px]'}`}>
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="h-8 w-8 text-yellow-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('offer.offerAlreadyProcessed')}</h2>
        </div>
      </div>
    );
  }

  const containerClass = compact ? 'p-4' : 'p-6';
  const theme = offerColorStyles(offer.primaryColor);

  return (
    <div
      className={`${containerClass} overflow-y-auto max-h-[700px]`}
      style={{ '--offer-color': theme.color }}
    >
      <h2 className={`font-bold text-gray-900 mb-4 ${compact ? 'text-xl' : 'text-2xl'}`}>
        {t('offer.yourInformation')}
      </h2>
      <p className="text-sm text-gray-600 mb-2">{t('offer.contactInfoHint')}</p>
      <p className="text-sm text-gray-600 mb-4">{t('offer.dataUsageNotice')}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <User className="inline h-4 w-4 mr-1" />
            {t('offer.fullName')}
          </label>
          <input
            type="text"
            name="name"
            value={customerInfo.name}
            onChange={handleCustomerInfoChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
            placeholder={t('offer.placeholderName')}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Mail className="inline h-4 w-4 mr-1" />
            {t('offer.emailAddress')}
          </label>
          <input
            type="email"
            name="email"
            value={customerInfo.email}
            onChange={handleCustomerInfoChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
            placeholder={t('offer.placeholderEmail')}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Phone className="inline h-4 w-4 mr-1" />
            {t('offer.phoneNumber')}
          </label>
          <input
            type="tel"
            name="phone"
            value={customerInfo.phone}
            onChange={handleCustomerInfoChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
            placeholder={t('offer.placeholderPhone')}
            required
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MapPin className="inline h-4 w-4 mr-1" />
            {t('offer.address')}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1">
            <input
              type="text"
              name="street"
              value={customerInfo.street}
              onChange={handleCustomerInfoChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input md:col-span-3"
              placeholder={t('offer.placeholderStreet')}
              required
            />
            {offer?.customerInfo?.locationZip ? (
              <div className="flex items-center gap-2 md:col-span-1">
                <span className="text-sm text-gray-600">{t('stepper.zipCode')}:</span>
                <span className="text-sm font-medium text-gray-900">{offer.customerInfo.locationZip}</span>
              </div>
            ) : (
              <input
                type="text"
                name="zip"
                value={customerInfo.zip}
                onChange={handleCustomerInfoChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input"
                placeholder={t('offer.placeholderZip')}
                required
              />
            )}
            <input
              type="text"
              name="city"
              value={customerInfo.city}
              onChange={handleCustomerInfoChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input md:col-span-2"
              placeholder={t('offer.placeholderCity')}
              required
            />
            <input
              type="text"
              name="country"
              value={customerInfo.country}
              onChange={handleCustomerInfoChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none offer-brand-input md:col-span-3"
              placeholder={t('offer.placeholderCountry')}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-6 flex-wrap">
        {showBackToChat && onBackToChat && (
          <button
            type="button"
            onClick={onBackToChat}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('offer.developerBackToChat')}
          </button>
        )}
        {showRejectButton && (
          <button
            onClick={handleRejectOffer}
            disabled={isSubmittingOffer}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmittingOffer ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                {t('offer.processing')}
              </>
            ) : (
              <>
                <X className="h-4 w-4 mr-2" />
                {t('offer.rejectOffer')}
              </>
            )}
          </button>
        )}
        <button
          onClick={handleAcceptOffer}
          disabled={!validateCustomerInfo() || isSubmittingOffer}
          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSubmittingOffer ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('offer.processing')}
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              {t('offer.acceptOffer')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ContactFormStep;
